import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { api } from '../api';
import type { MatchResult } from '../types';
import { MatchCard } from '../components/MatchCard';
import { loadLastResults, saveLastResults } from '../lastResults';

/** Shows results for a saved search (by id) or an ad-hoc run passed via router state. */
export function ResultsPage() {
  const { id } = useParams();
  const loc = useLocation();
  const state = loc.state as { results?: MatchResult[]; name?: string } | null;
  // Ad-hoc results arrive via router state; after a reload fall back to the stored copy.
  const stored = !id && !state?.results ? loadLastResults() : null;
  const [results, setResults] = useState<MatchResult[] | null>(state?.results ?? stored?.results ?? null);
  const [name, setName] = useState(state?.name ?? stored?.name ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.runSavedSearch(id).then((r) => { setResults(r.results); setName(r.search.name); }).catch((e) => setError(e.message));
  }, [id]);

  useEffect(() => {
    if (results) saveLastResults({ name, results, path: loc.pathname });
  }, [results, name, loc.pathname]);

  if (error) return <div className="error">{error}</div>;
  if (results === null) return <div className="spinner">Searching…</div>;

  return (
    <div className="stack">
      <div className="row between">
        <strong>{name}</strong>
        <span className="muted small">{results.length} match{results.length === 1 ? '' : 'es'}</span>
      </div>
      {id && <Link to={`/searches/${id}/edit`} className="small">Edit this search</Link>}
      {results.length === 0 ? (
        <div className="empty">No one matched these filters. Try a wider radius or age range, fewer instrument or shows filters, or add more bands to your profile.</div>
      ) : results.map((m) => <MatchCard key={m.user.id} m={m} />)}
    </div>
  );
}
